/**
 * Los documentos de una ficha: qué se dibuja, y cuándo no se dibuja nada.
 *
 * Vive en `src/lib` y no adentro de un módulo porque lo usan dos —la ficha de
 * una empresa y la de una persona— y ninguno es su dueño: los documentos salen
 * del correo y viven en Google Drive, que no es un módulo sino una integración
 * de la organización (`lib/puertas-de-extras.test.ts` es la regla).
 *
 * LA COMPUERTA ES `activo`, Y NADA MÁS. Una organización que no conectó Drive no
 * tiene documentos que mostrar ni los va a tener: para ella ni la pestaña ni la
 * sección existen, y las rutas de documentos no se piden. No es una lista vacía
 * —«todavía no hay nada», que es una respuesta legítima de una organización que
 * SÍ lo conectó—, es que no hay dónde.
 *
 * Módulo PURO: sin React y sin red. Lo que decide se prueba con una tabla.
 */

import type { DireccionDeDocumento, Documento, EstadoDeDrive } from '@/lib/api/contract';

/**
 * Si esta organización tiene Drive conectado.
 *
 * `undefined` es «todavía no se sabe» —la consulta en vuelo— o «no se pudo
 * saber» —falló, o el servidor todavía no tiene la ruta—, y los dos se leen como
 * NO: dibujar una pestaña y sacarla medio segundo después es un parpadeo que se
 * ve; no dibujarla y que aparezca cuando el dato llega, no. Es la misma
 * asimetría que `hayEquipo`.
 */
export const driveEstaActivo = (
  estado: Pick<EstadoDeDrive, 'activo'> | null | undefined,
): boolean => estado?.activo === true;

/** Lo que dice una lista sin documentos, en una organización que sí tiene Drive. */
export const SIN_DOCUMENTOS = {
  titulo: 'Todavía no hay documentos',
  descripcion: 'Aparecen solos cuando llega o sale un correo con adjuntos.',
} as const;

/** Qué dibuja una lista de documentos. Una sola de las cuatro, siempre. */
export type VistaDeDocumentos = 'cargando' | 'error' | 'vacia' | 'lista';

/**
 * Cuál de los cuatro estados toca.
 *
 * EL ERROR VA ANTES QUE EL VACÍO, y es lo único delicado de esta función: una
 * consulta que falló no trae documentos, y leída al revés la ficha diría
 * «Todavía no hay documentos» sobre una empresa que tiene veinte. Cargando va
 * primero porque mientras se pregunta no hay nada cierto que decir.
 */
export function vistaDeDocumentos(lista: {
  loading: boolean;
  error: unknown;
  items: readonly unknown[];
}): VistaDeDocumentos {
  if (lista.loading) return 'cargando';
  if (lista.error) return 'error';
  return lista.items.length === 0 ? 'vacia' : 'lista';
}

/**
 * «Enviado» o «Recibido», con palabras.
 *
 * La dirección se dice con texto y no sólo con un color: es lo que distingue lo
 * que la empresa ya tiene de lo que nos mandó, y un color solo no se lo dice a
 * quien no lo distingue ni a un lector de pantalla. Vacío cuando no se sabe.
 */
export const etiquetaDeDireccion = (direccion: DireccionDeDocumento | null): string =>
  direccion === 'enviado' ? 'Enviado' : direccion === 'recibido' ? 'Recibido' : '';

/**
 * El nombre que se lee en la fila.
 *
 * El original —como viajó en el correo— y no el de Drive, que antepone la fecha
 * y agrega la dirección: en la fila esas dos cosas ya están dichas al lado, y
 * repetidas dentro del nombre lo empujan fuera de la vista.
 */
export const nombreVisible = (documento: Pick<Documento, 'nombre' | 'nombreOriginal'>): string =>
  documento.nombreOriginal || documento.nombre;

const UNIDADES = ['KB', 'MB', 'GB'] as const;
const decimal = new Intl.NumberFormat('es-CL', { maximumFractionDigits: 1 });

/** Lo que se dibuja cuando no se sabe cuánto pesa. Nunca «0 B». */
export const SIN_TAMANO = '—';

/**
 * El tamaño, como lo diría una persona: «512 B», «137 KB», «3,3 MB».
 *
 * `null` —el servidor no lo dijo— es «—» y no «0 B»: un archivo vacío es un
 * dato, y uno del que no se sabe el peso es otra cosa. Los kilobytes van sin
 * decimales —nadie compara 136,5 KB con 137— y de ahí para arriba con uno.
 */
export function tamanoLegible(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return SIN_TAMANO;
  if (bytes < 1024) return `${Math.round(bytes)} B`;

  // Se compara lo que se va a DIBUJAR, ya redondeado: comparando el valor crudo,
  // 1.048.570 bytes salía «1024 KB» en vez de «1 MB».
  const redondear = (valor: number, unidad: number): number =>
    unidad === 0 ? Math.round(valor) : Math.round(valor * 10) / 10;

  let valor = bytes / 1024;
  let unidad = 0;
  while (unidad < UNIDADES.length - 1 && redondear(valor, unidad) >= 1024) {
    valor /= 1024;
    unidad += 1;
  }
  return `${decimal.format(redondear(valor, unidad))} ${UNIDADES[unidad]}`;
}

/**
 * «en 2 correos», cuando el mismo archivo apareció en más de uno.
 *
 * Con una sola aparición no se dice nada: es lo normal, y «en 1 correo» en cada
 * fila es ruido. Con más, explica por qué hay un archivo y no dos — el mismo
 * correo visto desde dos casillas, o un adjunto reenviado.
 */
export const enCuantosCorreos = (apariciones: number): string =>
  apariciones > 1 ? `en ${apariciones} correos` : '';

/** «1 documento», «4 documentos». */
export const cuantosDocumentos = (cantidad: number): string =>
  `${cantidad} ${cantidad === 1 ? 'documento' : 'documentos'}`;
